import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Paper,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Skeleton,
} from '@mui/material';
import {
  Business,
  Home,
  CalendarToday,
  TrendingUp,
  Visibility,
  CheckCircle,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Property, Booking, propertyApi, bookingApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalProperties: number;
  totalUnits: number;
  totalBookings: number;
  activeBookings: number;
  completedBookings: number;
}

export const DashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = user?.role === 'admin';
  const { t } = useTranslation();

  const [stats, setStats] = useState<DashboardStats>({
    totalProperties: 0,
    totalUnits: 0,
    totalBookings: 0,
    activeBookings: 0,
    completedBookings: 0,
  });
  const [recentProperties, setRecentProperties] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Fetch properties and bookings in parallel
        const [properties, bookings] = await Promise.all([
          propertyApi.getAll(),
          bookingApi.getAll()
        ]);
        
        // Calculate stats
        const totalUnits = properties.reduce((sum, property) => sum + (property.units?.length || 0), 0);
        
        // Calculate booking statistics
        const totalBookings = Array.isArray(bookings) ? bookings.length : 0;
        
        // Get today's date for comparison
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        let activeBookings = 0;
        let completedBookings = 0;
        
        if (Array.isArray(bookings)) {
          bookings.forEach((booking: Booking) => {
            const endDate = new Date(booking.end_date);
            endDate.setHours(23, 59, 59, 999);
            
            if (booking.status !== 'cancelled') {
              if (endDate >= today) {
                // Active: All confirmed future + current bookings (not cancelled, end_date >= today)
                activeBookings++;
              } else {
                // Completed: Past bookings that have finished (end_date < today, not cancelled)
                completedBookings++;
              }
            }
          });
        }
        
        setStats({
          totalProperties: properties.length,
          totalUnits,
          totalBookings,
          activeBookings,
          completedBookings,
        });
        
        setRecentProperties(properties.slice(0, 6));
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        // Set fallback values on error
        setStats({
          totalProperties: 0,
          totalUnits: 0,
          totalBookings: 0,
          activeBookings: 0,
          completedBookings: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactElement;
    color: string;
    subtitle?: string;
  }> = ({ title, value, icon, color, subtitle }) => (
    <Card
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        border: `1px solid ${color}20`,
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 8px 25px ${color}20`,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 'bold', color: color, mb: 1 }}>
              {loading ? <Skeleton width={60} /> : value}
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.primary', mb: 0.5 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              bgcolor: color,
              width: 56,
              height: 56,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );

  const PropertyCard: React.FC<{ property: Property }> = ({ property }) => (
    <Card
      sx={{
        height: '100%',
        cursor: 'pointer',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        },
      }}
      onClick={() => navigate(`/properties/${property.id}`)}
    >
      <Box
        sx={{
          height: 200,
          backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Business sx={{ fontSize: 48, color: 'white', opacity: 0.7 }} />
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
          }}
        >
          <Chip
            label={property.type}
            size="small"
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              color: 'text.primary',
              fontWeight: 'bold',
              textTransform: 'capitalize',
            }}
          />
        </Box>
      </Box>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
          {property.name}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          {property.address}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Home fontSize="small" color="action" />
            <Typography variant="body2">
              {property.units?.length || 0} {t('units').toLowerCase()}
            </Typography>
          </Box>
          <IconButton
            size="small"
            sx={{
              ml: 'auto',
              color: 'primary.main',
            }}
          >
            <Visibility fontSize="small" />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          {t('welcome_back')}, {user?.name}! 👋
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t('whats_happening_today')}
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('total_properties')}
            value={stats.totalProperties}
            icon={<Business />}
            color={theme.palette.primary.main}
            subtitle={t('properties_managed')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('total_units')}
            value={stats.totalUnits}
            icon={<Home />}
            color={theme.palette.secondary.main}
            subtitle={t('available_units')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('total_bookings')}
            value={stats.totalBookings}
            icon={<CalendarToday />}
            color={theme.palette.success.main}
            subtitle={t('all_time_bookings')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={t('active_bookings')}
            value={stats.activeBookings}
            icon={<TrendingUp />}
            color={theme.palette.warning.main}
            subtitle={t('currently_active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed Bookings"
            value={stats.completedBookings}
            icon={<CheckCircle />}
            color={theme.palette.info.main}
            subtitle="Finished stays"
          />
        </Grid>
      </Grid>

      {/* Recent Properties */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
            {isAdmin ? t('all_properties') : t('your_properties')}
          </Typography>
          <Chip
            label={`${recentProperties.length} ${t('properties').toLowerCase()}`}
            color="primary"
            variant="outlined"
          />
        </Box>
        
        {loading ? (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card>
                  <Skeleton variant="rectangular" height={200} />
                  <CardContent>
                    <Skeleton variant="text" height={32} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="text" width="60%" height={20} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : recentProperties.length > 0 ? (
          <Grid container spacing={3}>
            {recentProperties.map((property) => (
              <Grid item xs={12} sm={6} md={4} key={property.id}>
                <PropertyCard property={property} />
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            <Business sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {t('no_properties_yet')}
            </Typography>
            <Typography variant="body2">
              {isAdmin
                ? t('start_creating_first_property')
                : t('ask_admin_assign_properties')}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};
